import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { SofiaAvatar } from "@/components/sofia-avatar"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchInput, SearchItem, SuggestionSearch } from "@/components/ui/search"
import { AgentOption, useAgentOptions } from "@/hooks/useAgentOptions"
import { cn } from "@/lib/utils"
import { SearchOption } from "@/modules/patients/components/search-option"
import {
  useWaitingRoomFilters,
  useWaitingRoomFiltersContext,
} from "@/modules/waiting-room/contexts/waiting-room-filters-context"

type AgentFilterProps = React.ComponentPropsWithoutRef<typeof Chip>

const NOBODY = "NOBODY"

const AgentFilter = (props: AgentFilterProps) => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState("")
  const currentUserName = useUser().userName
  const setFilters = useWaitingRoomFiltersContext((state) => state.setFilters)

  const { agents: agentsInput } = useWaitingRoomFilters().filters
  const hasUnassigned = agentsInput?.hasUnassigned
  const agents = agentsInput?.userNames ?? []

  const agentsDisplay = hasUnassigned ? agents.concat(NOBODY) : agents
  const hasAgentsDisplay = agentsDisplay.length > 0

  const agentOptions = useAgentOptions()

  const allOptions = useMemo(() => {
    const nobodyOption: AgentOption = {
      label: t("scheduling.waitingRoom.filters.agentFilter.nobody"),
      icon: (
        <SofiaAvatar firstName={t("scheduling.waitingRoom.filters.agentFilter.nobody")} lastName={null} size="md" />
      ),
      value: NOBODY,
      role: null,
      specialties: "",
      isDeleted: false,
    }

    // sort the current user to the top
    const options = agentOptions.toSorted((a) => (a.value === currentUserName ? -1 : 0))
    options.unshift(nobodyOption)

    return options
  }, [currentUserName, agentOptions, t])

  const filteredOptions =
    searchValue.length < 3
      ? allOptions
      : allOptions.filter((option) => {
          const searchTerms = searchValue.trim().toLowerCase().split(" ")
          const label = option.label.toLowerCase()
          return searchTerms.some((term) => label.includes(term))
        })

  const hasResults = filteredOptions.length > 0

  const resetFilters = () => {
    setFilters({ agents: null })
    setSearchValue("")
  }

  const handleSelectOption = (option: AgentOption) => {
    const newAgents = agentsDisplay.includes(option.value)
      ? agentsDisplay.filter((agent) => agent !== option.value)
      : [...agentsDisplay, option.value]

    if (newAgents.length === 0) {
      setFilters({ agents: null })
      return
    }

    const hasUnassigned = newAgents.includes(NOBODY)
    const userNames = newAgents.filter((agent) => agent !== NOBODY)

    setFilters({ agents: { userNames, hasUnassigned } })
  }

  const agentLabel = useMemo(() => {
    const agentsOptionFilter = allOptions.filter((option) => agentsDisplay?.includes(option.value))

    if (agentsOptionFilter.length <= 2) return agentsOptionFilter.map((option) => option.label).join(", ")

    return agentsOptionFilter
      .slice(0, 2)
      .map((option) => option.label)
      .join(", ")
      .concat(", ...")
  }, [allOptions, agentsDisplay])

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer">
        <Chip
          checked={hasAgentsDisplay}
          className="data-[state=checked]:hover:bg-primary-surface-hovered flex h-8 items-center gap-0.5"
          {...props}>
          <Check
            className={cn("overflow-hidden transition-all duration-200", hasAgentsDisplay ? "w-5" : "w-0")}
            size={20}
          />
          <div className="truncate px-1.5">
            {hasAgentsDisplay
              ? `${agentLabel} (${agentsDisplay.length})`
              : t("scheduling.waitingRoom.filters.agentFilter.agent")}
          </div>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="rounded-lg" align="start">
        <SuggestionSearch className="w-fit">
          <div className="p-2 pb-0">
            <SearchInput
              onKeyDown={(e) => {
                e.stopPropagation()
              }}
              data-testid="agents-input"
              value={searchValue}
              placeholder={t("scheduling.waitingRoom.filters.agentFilter.searchMedgateUser")}
              autoComplete="off"
              onChange={({ target: { value } }) => {
                setSearchValue(value)
              }}
              onClear={() => {
                setSearchValue("")
              }}
            />
            <DropdownMenuSeparator className="bg-border -mx-2 mt-2 mb-0" />
          </div>
          <div className="border-border max-h-80 border-b" data-testid="agents-search-section">
            {!hasResults && (
              <SearchItem
                disabled
                className="text-on-surface-subdued z-30 mx-2 cursor-default overflow-hidden rounded-md py-1.5">
                {t("common.nothingFound")}
              </SearchItem>
            )}
            <ScrollArea className="h-77.5">
              <div className="grid max-w-81 gap-0.5 first:mt-2 last:mb-2">
                {hasResults &&
                  filteredOptions.map((option, index) => {
                    const { value, label, icon, specialties } = option
                    return (
                      <div
                        key={value}
                        className={cn(
                          ((index === 1 && currentUserName === filteredOptions[1].value) ||
                            (index === 0 && currentUserName === filteredOptions[0].value)) &&
                            "border-border mb-2 border-b pb-2",
                        )}>
                        <SearchOption
                          checked={agentsDisplay.includes(value)}
                          searchValue={searchValue}
                          value={value}
                          title={label}
                          onSelect={() => handleSelectOption(option)}
                          icon={icon}
                          className="mx-2 px-2 py-1"
                          description={specialties}
                        />
                      </div>
                    )
                  })}
              </div>
            </ScrollArea>
          </div>
          <DropdownMenuSeparator className="m-0" />
          <div className="flex justify-end">
            <div className="p-2">
              <Button
                onClick={resetFilters}
                data-testid="agents-reset-button"
                variant="tertiary"
                className="h-10 rounded-full px-2 py-0.5 leading-5 font-medium">
                {t("common.button.reset")}
              </Button>
            </div>
          </div>
        </SuggestionSearch>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { AgentFilter }
